import { orderValues } from "./valueListValue";
/** A value with no parent, or whose parent is missing from the list. */
function isTopLevel(value, byCode) {
    return value.parentInternalCode === null || !byCode.has(value.parentInternalCode);
}
/**
 * The category a value is shown under. Only a top-level value is a category, so a value whose
 * parent is missing, is itself a sub-value (a third level) or points back at it (a loop) is shown
 * at the top level, never dropped.
 */
function parentOf(value, byCode) {
    const parent = value.parentInternalCode === null ? undefined : byCode.get(value.parentInternalCode);
    return parent && isTopLevel(parent, byCode) ? parent : null;
}
/**
 * Every value in display order, each category followed by its sub-values. Both levels follow the
 * list's sort mode. The path position applies to a manual two-level list only: a flat list keeps
 * its stored order (unchanged), an alphabetical list has no rank to show.
 */
export function buildHierarchyRows(values, sortMode, locale) {
    const byCode = new Map(values.map((value) => [value.internalCode, value]));
    const ordered = orderValues(values, sortMode, locale);
    const roots = ordered.filter((value) => parentOf(value, byCode) === null);
    const childrenOf = new Map();
    for (const value of ordered) {
        const parent = parentOf(value, byCode);
        if (parent)
            childrenOf.set(parent.internalCode, [...(childrenOf.get(parent.internalCode) ?? []), value]);
    }
    const hiddenByParent = (value) => value.parentInternalCode !== null && (byCode.get(value.parentInternalCode)?.isHidden ?? false);
    const twoLevel = childrenOf.size > 0;
    const manual = sortMode === "Manual";
    return roots.flatMap((root, index) => {
        const children = childrenOf.get(root.internalCode) ?? [];
        const rootPosition = !manual ? "" : twoLevel ? String(index + 1) : String(root.sortOrder);
        return [
            {
                value: root,
                parent: null,
                inheritedHidden: hiddenByParent(root),
                position: rootPosition,
                rank: index + 1,
                siblings: roots.length,
                isContext: false,
            },
            ...children.map((child, childIndex) => ({
                value: child,
                parent: root,
                inheritedHidden: hiddenByParent(child),
                position: manual ? `${index + 1}.${childIndex + 1}` : "",
                rank: childIndex + 1,
                siblings: children.length,
                isContext: false,
            })),
        ];
    });
}
/** The value as its row presents it: a sub-value of a hidden category reads as hidden. */
export function effectiveValue(row) {
    return row.inheritedHidden ? { ...row.value, isHidden: true } : row.value;
}
/**
 * The rows `matches` keeps. A kept sub-value whose category is not kept brings it along, marked
 * as context, so a sub-value is never shown without its category.
 */
export function filterHierarchyRows(rows, matches) {
    const kept = new Set(rows.filter(matches).map((row) => row.value.internalCode));
    const result = [];
    let pendingRoot = null;
    for (const row of rows) {
        if (row.parent === null) {
            pendingRoot = row;
            if (kept.has(row.value.internalCode)) {
                result.push(row);
                pendingRoot = null;
            }
            continue;
        }
        if (!kept.has(row.value.internalCode))
            continue;
        if (pendingRoot && pendingRoot.value.internalCode === row.parent.internalCode) {
            result.push({ ...pendingRoot, isContext: true });
            pendingRoot = null;
        }
        result.push(row);
    }
    return result;
}
